import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { joinWaitlist, getWaitlistCount, submitContact } from "@/lib/api";
import {
  ArrowRight,
  Sparkles,
  Compass,
  Send,
  Linkedin,
  Wand2,
  ShieldCheck,
  Loader2,
  Check,
  Mail,
  MessageSquare,
} from "lucide-react";
import { toast } from "sonner";

export default function Landing() {
  const [waitlistCount, setWaitlistCount] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [tier, setTier] = useState("");
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);
  const waitlistRef = useRef(null);

  // Contact form state
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactMessage, setContactMessage] = useState("");
  const [contactCompanyWebsite, setContactCompanyWebsite] = useState(""); // honeypot
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSent, setContactSent] = useState(false);

  useEffect(() => {
    getWaitlistCount()
      .then((d) => setWaitlistCount(d?.count ?? 0))
      .catch(() => setWaitlistCount(0));
  }, []);

  const scrollToWaitlist = (preselectTier = "") => {
    if (preselectTier) setTier(preselectTier);
    waitlistRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const onJoin = async (e) => {
    e?.preventDefault?.();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Please enter a valid email.");
      return;
    }
    setJoining(true);
    try {
      await joinWaitlist({
        email: email.trim(),
        role: role.trim(),
        tier_interest: tier,
        referrer: typeof document !== "undefined" ? document.referrer : "",
      });
      setJoined(true);
      toast.success("You're on the list. We'll be in touch.");
      const next = await getWaitlistCount();
      setWaitlistCount(next?.count ?? null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not join the waitlist");
    } finally {
      setJoining(false);
    }
  };

  const onContactSubmit = async (e) => {
    e?.preventDefault?.();
    const name = contactName.trim();
    const email = contactEmail.trim();
    const message = contactMessage.trim();
    if (!name) {
      toast.error("Please enter your name.");
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Please enter a valid email.");
      return;
    }
    if (message.length < 5) {
      toast.error("Please write a short message.");
      return;
    }
    setContactSubmitting(true);
    try {
      await submitContact({
        name,
        email,
        message,
        referrer: typeof document !== "undefined" ? document.referrer : "",
        company_website: contactCompanyWebsite, // honeypot
      });
      setContactSent(true);
      toast.success("Thanks — we\u2019ll be in touch within 48h.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send your message");
    } finally {
      setContactSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#EDEDED] grain overflow-x-hidden">
      {/* TOP NAV */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-[#0A0A0A]/70 border-b border-[#222]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-3 sm:py-4 flex items-center justify-between gap-3">
          <Link to="/" className="font-serif text-xl sm:text-2xl tracking-tight shrink-0" data-testid="lp-brand">
            Vantage<span className="text-[#CCFF00]"> </span>Hub
          </Link>
          <div className="hidden md:flex items-center gap-8 uppercase-mono text-[#8a8a8a]">
            <a href="#how" className="hover:text-[#ededed] transition-colors">How</a>
            <a href="#built-for" className="hover:text-[#ededed] transition-colors">Who it&apos;s for</a>
            <a href="#features" className="hover:text-[#ededed] transition-colors">Features</a>
            <a href="#about" className="hover:text-[#ededed] transition-colors">About</a>
            <a href="#pricing" className="hover:text-[#ededed] transition-colors">Pricing</a>
            <a href="#contact" className="hover:text-[#ededed] transition-colors">Contact</a>
          </div>
          <button
            onClick={() => scrollToWaitlist()}
            data-testid="lp-nav-cta"
            className="btn-accent px-3 sm:px-4 py-2 uppercase tracking-widest text-[10px] sm:text-[11px] whitespace-nowrap"
          >
            Join waitlist
          </button>
        </div>
      </nav>

      {/* HERO */}
      <header className="pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6 lg:px-12" data-testid="lp-hero">
        <div className="max-w-7xl mx-auto">
          <div className="uppercase-mono text-[#8a8a8a] flex items-center gap-3 text-[10px] sm:text-[11px]">
            <span className="inline-block w-2 h-2 bg-[#CCFF00] blink shrink-0" />
            <span>Built for independent creative professionals · v0 private beta</span>
          </div>
          <h1 className="font-serif font-light leading-[0.95] sm:leading-[0.92] tracking-tight mt-6 text-[33px] sm:text-[54px] md:text-[66px] lg:text-[111px]">
            Research-backed leads for Freelance Creative Professionals who&apos;d rather
            <br />
            <span className="text-[#CCFF00] italic">be building.</span>
          </h1>
          <div className="mt-8 sm:mt-10 grid grid-cols-12 gap-6 items-end">
            <p className="col-span-12 lg:col-span-8 text-[#ededed] text-base sm:text-lg lg:text-xl max-w-2xl leading-relaxed">
              Find real, verified prospects in industries that actually hire creatives. Write outreach
              that sounds like <em>you</em>. Send from your own inbox. No spammy templates, no
              &ldquo;Hi&nbsp;{`{{firstname}}`}&rdquo; tools.
            </p>
            <div className="col-span-12 lg:col-span-4 lg:text-right flex flex-col lg:items-end gap-3">
              <button
                onClick={() => scrollToWaitlist()}
                data-testid="lp-hero-cta"
                className="btn-accent px-6 py-3 uppercase tracking-widest text-[12px] inline-flex items-center justify-center gap-2 self-start lg:self-end w-full sm:w-auto"
              >
                Join the waitlist <ArrowRight size={14} strokeWidth={1.5} />
              </button>
              {waitlistCount > 0 && (
                <span className="uppercase-mono text-[#555]">
                  {waitlistCount} on the list
                </span>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* LIME MARQUEE */}
      <div className="bg-[#CCFF00] text-black overflow-hidden border-y border-[#222]">
        <div className="flex whitespace-nowrap font-mono uppercase tracking-widest text-[12px] py-2.5 animate-[marquee_45s_linear_infinite]">
          {["marquee-a", "marquee-b"].map((rowKey) => (
            <div key={rowKey} className="flex">
              {[
                "Vantage Hub-verified leads",
                "AI drafts in your voice",
                "Send from your own Gmail",
                "LinkedIn copy-paste connector",
                "Editorial pipeline",
                "Activity timeline",
                "Vantage Hub email enrichment",
                "Multi-line signatures",
                "Soft-CTA emails",
              ].map((s) => (
                <span key={`${rowKey}-${s}`} className="px-8 inline-flex items-center gap-2">
                  <span className="opacity-50">↗</span>
                  {s}
                  <span className="opacity-40">·</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* THE PROBLEM */}
      <section className="px-4 sm:px-6 lg:px-12 py-16 sm:py-24 border-b border-[#222]">
        <div className="max-w-7xl mx-auto grid grid-cols-12 gap-6 sm:gap-8">
          <div className="col-span-12 lg:col-span-3">
            <div className="uppercase-mono text-[#8a8a8a]">01 — The problem</div>
          </div>
          <div className="col-span-12 lg:col-span-9">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-6xl leading-[1.05] tracking-tight">
              Every CRM was built by sales people, for sales people. <span className="text-[#CCFF00]">None of them sound like you.</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#222] mt-10 sm:mt-12">
              {[
                {
                  title: "Generic lead lists.",
                  body: "Apollo, ZoomInfo, HubSpot — built for B2B SaaS reps blasting CXOs. Not for finding the actual person at the Whitney who commissions installations.",
                },
                {
                  title: "Template-mail energy.",
                  body: "Mailshake / Lemlist / Apollo Email rely on merge-tag templates. Recipients smell it immediately. Open rates collapse.",
                },
                {
                  title: "UI built by engineers.",
                  body: "Aesthetically barren dashboards designed to make sales-ops happy. You'd rather not look at it. So you don't. So you don't follow up.",
                },
              ].map((c) => (
                <div key={c.title} className="bg-[#0A0A0A] p-6 sm:p-8">
                  <div className="font-serif text-xl sm:text-2xl">{c.title}</div>
                  <p className="text-sm text-[#8a8a8a] mt-3 leading-relaxed">{c.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="px-4 sm:px-6 lg:px-12 py-16 sm:py-24 border-b border-[#222]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-12 gap-6 sm:gap-8 items-end mb-10 sm:mb-16">
            <div className="col-span-12 lg:col-span-4">
              <div className="uppercase-mono text-[#8a8a8a]">02 — How it works</div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-6xl leading-[1.05] tracking-tight mt-4">
                Three honest steps.
              </h2>
            </div>
            <p className="col-span-12 lg:col-span-8 text-[#ededed] text-base sm:text-lg leading-relaxed lg:pl-12">
              No &ldquo;AI-powered prospecting cloud platform.&rdquo; Just three steps that respect your time
              and inbox reputation.
            </p>
          </div>

          <ol className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-[#222]">
            {[
              {
                n: "01",
                title: "We search the actual web.",
                copy: "Vantage Hub reads live press releases, museum announcements, festival lineups, agency hires. You get real companies doing real projects this month — not a 12-month-stale database.",
                icon: Compass,
              },
              {
                n: "02",
                title: "We find the real human.",
                copy: "Vantage Hub extracts only named decision-makers from the snippets and enriches them with the verified email. No fabricated names. No 'team at X' placeholders. Every lead carries source URLs you can click.",
                icon: ShieldCheck,
              },
              {
                n: "03",
                title: "You write it. We polish it. You send it.",
                copy: "AI draft opens with your verbatim opener, weaves in one specific thing about their work, closes with a soft single-question CTA. You edit inline, hit Send Now — it ships from your own Gmail. No third-party send relays. No spam folder.",
                icon: Send,
              },
            ].map((s) => {
              const Icon = s.icon;
              return (
                <li key={s.n} className="bg-[#0A0A0A] p-6 sm:p-10">
                  <div className="flex items-center justify-between">
                    <Icon size={28} strokeWidth={1.2} className="text-[#CCFF00]" />
                    <span className="uppercase-mono text-[#8a8a8a]">{s.n}</span>
                  </div>
                  <div className="font-serif text-2xl sm:text-3xl mt-6 sm:mt-8 leading-tight">{s.title}</div>
                  <p className="text-sm text-[#8a8a8a] mt-4 leading-relaxed">{s.copy}</p>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* BUILT FOR */}
      <section id="built-for" className="px-4 sm:px-6 lg:px-12 py-16 sm:py-24 border-b border-[#222] bg-[#CCFF00] text-black">
        <div className="max-w-7xl mx-auto grid grid-cols-12 gap-6 sm:gap-8 items-start">
          <div className="col-span-12 lg:col-span-4">
            <div className="uppercase-mono text-black/60">03 — Built for</div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-6xl leading-[1.05] tracking-tight mt-4 text-black">
              Independent creative professionals.
            </h2>
            <p className="mt-6 max-w-md text-black/80 leading-relaxed">
              The tool you wished existed when you were sending the third LinkedIn DM that month.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-8 grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
            {[
              "Creative technologists",
              "Motion designers",
              "3D / Real-time artists",
              "Brand consultants",
              "Sound designers",
              "Illustrators",
              "Interactive directors",
              "Indie product designers",
              "Web designers",
              "Type designers",
              "Boutique studios (2–10)",
              "Freelance directors",
            ].map((r) => (
              <div
                key={r}
                className="border border-black/20 px-3 sm:px-4 py-3 sm:py-4 font-mono text-xs sm:text-sm hover:bg-black hover:text-[#CCFF00] transition-colors cursor-default"
              >
                · {r}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="px-4 sm:px-6 lg:px-12 py-16 sm:py-24 border-b border-[#222]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-12 gap-6 sm:gap-8 items-end mb-10 sm:mb-16">
            <div className="col-span-12 lg:col-span-4">
              <div className="uppercase-mono text-[#8a8a8a]">04 — Features</div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-6xl leading-[1.05] tracking-tight mt-4">
                What&apos;s in the box.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#222]">
            {[
              { icon: Compass, t: "Real-web discovery", b: "Vantage Hub's two-pass research pipeline finds named decision-makers, not placeholders." },
              { icon: ShieldCheck, t: "Verified emails", b: "Email enrichment with confidence score and verification status badge per lead." },
              { icon: Wand2, t: "AI drafts in your voice", b: "Verbatim opener + soft single-question CTA + your multi-line signature, preserved exactly." },
              { icon: Send, t: "Send from your Gmail", b: "Native SMTP send. Your inbox reputation. Your replies. No third-party relay or footer." },
              { icon: Linkedin, t: "LinkedIn deep-link", b: "Generate a 250-char connect note, copy + open profile in one click. ToS-safe." },
              { icon: Sparkles, t: "Editorial pipeline", b: "Drag-and-drop kanban across seven stages. Activity timeline per prospect. No spreadsheet brain damage." },
            ].map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.t} className="bg-[#0A0A0A] p-6 sm:p-8 hover:bg-[#101010] transition-colors">
                  <Icon size={22} strokeWidth={1.4} className="text-[#CCFF00]" />
                  <div className="font-serif text-xl sm:text-2xl mt-5 sm:mt-6">{f.t}</div>
                  <p className="text-sm text-[#8a8a8a] mt-3 leading-relaxed">{f.b}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PULL QUOTE */}
      <section className="px-4 sm:px-6 lg:px-12 py-20 sm:py-32 border-b border-[#222]">
        <div className="max-w-5xl mx-auto text-center">
          <div className="uppercase-mono text-[#8a8a8a]">A tool by a freelancer · for freelancers</div>
          <blockquote className="font-serif text-3xl sm:text-4xl lg:text-6xl leading-[1.1] sm:leading-[1.05] tracking-tight mt-6 sm:mt-8">
            <span className="text-[#CCFF00]">&ldquo;</span>
            Outbound used to feel like writing into a void. Now it feels like sending letters to people whose work I&apos;ve actually read about this morning.
            <span className="text-[#CCFF00]">&rdquo;</span>
          </blockquote>
          <div className="uppercase-mono text-[#8a8a8a] mt-8 sm:mt-10">— Early user · creative technologist</div>
        </div>
      </section>

      {/* ABOUT US */}
      <section id="about" className="px-4 sm:px-6 lg:px-12 py-16 sm:py-24 border-b border-[#222]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-12 gap-6 sm:gap-8 items-end mb-10 sm:mb-16">
            <div className="col-span-12 lg:col-span-4">
              <div className="uppercase-mono text-[#8a8a8a]">05 — About us</div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-6xl leading-[1.05] tracking-tight mt-4">
                Built for creative professionals, <span className="text-[#CCFF00]">by creative technologists.</span>
              </h2>
            </div>
            <p className="col-span-12 lg:col-span-8 text-[#ededed] text-base sm:text-lg leading-relaxed lg:pl-12 max-w-2xl">
              We&apos;re a small team obsessed with one question: why does the business side of creative work feel
              so much worse than the work itself? We&apos;ve all sat at the same desk &mdash; one tab open to a
              Houdini render, the other to a 17-column lead spreadsheet that drains the day. Vantage Hub is what we
              built so that tab can finally close.
            </p>
          </div>

          {/* Manifesto strip */}
          <div className="mt-12 sm:mt-16 border border-[#222] p-6 sm:p-10 lg:p-14">
            <div className="uppercase-mono text-[#8a8a8a]">Our manifesto</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-10 mt-6">
              <div>
                <div className="font-serif text-2xl sm:text-3xl lg:text-4xl leading-tight">
                  Creativity is a business decision before it is an aesthetic one.
                </div>
              </div>
              <div className="text-[#ededed] leading-relaxed">
                <p>
                  We believe independent creative professionals deserve the same operational leverage that venture-funded
                  studios get &mdash; without sacrificing the voice, taste and craft that made clients want to hire them
                  in the first place.
                </p>
                <p className="mt-4">
                  So we build software that disappears behind the work. Tools that respect inbox reputations,
                  honour personal voice, and surface the right prospect at the right moment &mdash; then get out of the way
                  so you can go back to making the thing.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING TEASER */}
      <section id="pricing" className="px-4 sm:px-6 lg:px-12 py-16 sm:py-24 border-b border-[#222]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-12 gap-6 sm:gap-8 items-end mb-10 sm:mb-16">
            <div className="col-span-12 lg:col-span-6">
              <div className="uppercase-mono text-[#8a8a8a]">06 — Pricing</div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-6xl leading-[1.05] tracking-tight mt-4">
                Honest, monthly, cancel-any-time.
              </h2>
            </div>
            <p className="col-span-12 lg:col-span-6 text-[#8a8a8a] lg:text-right text-sm sm:text-base">
              First 50 waitlist members get <span className="text-[#CCFF00]">3 months free</span>{" "}
              + a public spotlight on the studio site.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#222]">
            {[
              {
                name: "Free",
                price: "$0",
                tag: "kick the tires",
                features: ["5 verified leads / mo", "AI drafts only", "No sending", "Vantage Hub verification badge"],
                cta: "Get notified",
                tier: "free",
              },
              {
                name: "Solo",
                price: "$29",
                tag: "the freelancer plan",
                features: ["50 verified leads / mo", "500 sends via your Gmail", "LinkedIn connector", "Full pipeline + activity"],
                cta: "Reserve Solo",
                tier: "solo",
                highlight: true,
              },
              {
                name: "Studio",
                price: "$79",
                tag: "small studios",
                features: ["200 verified leads / mo", "Unlimited sends", "Batch mode", "CSV import / export"],
                cta: "Reserve Studio",
                tier: "studio",
              },
              {
                name: "BYOK",
                price: "$19",
                tag: "bring your own keys",
                features: ["Connect your own data + email keys", "Pay only for the app + AI", "Highest margin, no quota risk"],
                cta: "Reserve BYOK",
                tier: "byok",
              },
            ].map((p) => (
              <div
                key={p.name}
                className={`p-6 sm:p-8 flex flex-col gap-5 sm:gap-6 transition-colors ${
                  p.highlight ? "bg-[#CCFF00] text-black" : "bg-[#0A0A0A] hover:bg-[#101010]"
                }`}
              >
                <div>
                  <div className={`uppercase-mono ${p.highlight ? "text-black/60" : "text-[#8a8a8a]"}`}>
                    {p.tag}
                  </div>
                  <div className="font-serif text-2xl sm:text-3xl mt-1">{p.name}</div>
                </div>
                <div className="font-serif text-4xl sm:text-5xl leading-none tracking-tighter">
                  {p.price}
                  <span className={`text-base ml-2 ${p.highlight ? "text-black/60" : "text-[#555]"}`}>/ mo</span>
                </div>
                <ul className="space-y-2 text-sm flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check size={14} strokeWidth={2} className={p.highlight ? "text-black mt-0.5" : "text-[#CCFF00] mt-0.5"} />
                      <span className={p.highlight ? "text-black" : "text-[#ededed]"}>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => scrollToWaitlist(p.tier)}
                  data-testid={`lp-tier-cta-${p.tier}`}
                  className={`uppercase tracking-widest text-[11px] py-3 px-4 transition-colors ${
                    p.highlight
                      ? "bg-black text-[#CCFF00] hover:bg-[#1a1a1a]"
                      : "border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black"
                  }`}
                >
                  {p.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-4 sm:px-6 lg:px-12 py-16 sm:py-24 border-b border-[#222]">
        <div className="max-w-5xl mx-auto">
          <div className="uppercase-mono text-[#8a8a8a] mb-6 sm:mb-8">07 — Quick answers</div>
          <div className="divide-y divide-[#222] border-y border-[#222]">
            {[
              { q: "Are the leads actually real?", a: "Yes. Vantage Hub's two-pass research pipeline finds named people in live press, hires, and project announcements. Every lead in your pipeline carries source URLs you can click to verify. We drop any candidate that can't be tied to a real name in a real source." },
              { q: "Will my Gmail get flagged for spam?", a: "Not the way most tools get you flagged. We send through your own SMTP (not a relay), no merge-tag templates, no tracking pixels by default, hard cap on volume per day. You stay under Gmail's behavioral thresholds." },
              { q: "Do you store my contacts?", a: "Yes, encrypted, only in your account. You can wipe all leads from your settings whenever. GDPR data export and delete on request — single email reply to a human." },
              { q: "Does the BYOK plan really save money?", a: "If you'd hit our quotas, yes — you only pay for the Vantage Hub app + AI generations, and you control all third-party usage directly. We'll show you the math during onboarding." },
            ].map((f) => (
              <details key={f.q} className="py-5 sm:py-6 group" data-testid={`faq-${f.q.slice(0, 12)}`}>
                <summary className="flex items-start justify-between gap-4 cursor-pointer list-none">
                  <span className="font-serif text-xl sm:text-2xl pr-2">{f.q}</span>
                  <span className="uppercase-mono text-[#CCFF00] group-open:rotate-45 transition-transform shrink-0 mt-1">+</span>
                </summary>
                <p className="text-[#8a8a8a] mt-4 leading-relaxed max-w-3xl text-sm sm:text-base">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* WAITLIST FORM */}
      <section ref={waitlistRef} id="waitlist" className="px-4 sm:px-6 lg:px-12 py-20 sm:py-32 border-b border-[#222]">
        <div className="max-w-3xl mx-auto text-center">
          <div className="uppercase-mono text-[#8a8a8a]">08 — Join the waitlist</div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-6xl leading-[1.05] tracking-tight mt-4">
            Be one of the first <span className="text-[#CCFF00]">50</span>.
          </h2>
          <p className="text-[#8a8a8a] mt-5 sm:mt-6 max-w-xl mx-auto leading-relaxed text-sm sm:text-base">
            We&apos;re onboarding the first cohort manually. Tell us who you are; we&apos;ll send your
            invite + your 3-month free trial when your seat opens.
          </p>

          {joined ? (
            <div className="mt-10 sm:mt-12 border border-[#CCFF00] p-8 sm:p-10" data-testid="lp-waitlist-success">
              <Check size={32} strokeWidth={1.5} className="mx-auto text-[#CCFF00]" />
              <div className="font-serif text-2xl sm:text-3xl mt-4">You&apos;re on the list.</div>
              <p className="text-[#8a8a8a] mt-3 break-words">
                We&apos;ll be in touch at <span className="text-[#ededed] font-mono">{email}</span> when your seat opens.
              </p>
            </div>
          ) : (
            <form onSubmit={onJoin} className="mt-10 sm:mt-12 space-y-4 text-left" data-testid="lp-waitlist-form">
              <div>
                <div className="uppercase-mono text-[#8a8a8a] mb-2">Your email</div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="lp-email-input"
                  placeholder="you@studio.com"
                  className="w-full px-4 py-4 text-base font-mono"
                  required
                />
              </div>
              <div>
                <div className="uppercase-mono text-[#8a8a8a] mb-2">What do you do? (optional)</div>
                <input
                  type="text"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  data-testid="lp-role-input"
                  placeholder="Freelance motion designer · NYC"
                  className="w-full px-4 py-4 text-base"
                />
              </div>
              {tier && (
                <div className="uppercase-mono text-[#CCFF00] flex items-center gap-2">
                  <span>Reserving:</span>
                  <span className="text-[#ededed]">{tier}</span>
                  <button
                    type="button"
                    onClick={() => setTier("")}
                    className="text-[#8a8a8a] hover:text-[#FF3333] ml-2"
                  >
                    clear
                  </button>
                </div>
              )}
              <button
                type="submit"
                disabled={joining}
                data-testid="lp-waitlist-submit"
                className="btn-accent w-full py-4 uppercase tracking-widest text-[12px] inline-flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {joining ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} strokeWidth={1.5} />}
                {joining ? "Adding you…" : "Reserve my spot"}
              </button>
              <p className="uppercase-mono text-[#555] text-center pt-4">
                No spam. One email when your invite opens. Unsubscribe with one click.
              </p>
            </form>
          )}
        </div>
      </section>

      {/* CONTACT */}
      <section
        id="contact"
        className="px-4 sm:px-6 lg:px-12 py-20 sm:py-28 border-b border-[#222]"
        data-testid="lp-contact-section"
      >
        <div className="max-w-4xl mx-auto">
          <div className="uppercase-mono text-[#8a8a8a] mb-3">09 — Contact</div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-6xl leading-[1.05] tracking-tight">
            Something on your mind? <span className="text-[#CCFF00]">Say hi.</span>
          </h2>
          <p className="text-[#8a8a8a] mt-4 max-w-2xl leading-relaxed text-sm sm:text-base">
            Partnership, press, feedback, or just a hello from a fellow maker — we read every note personally.
          </p>

          {contactSent ? (
            <div
              className="mt-10 border border-[#CCFF00] p-8 sm:p-10 bg-[#0F1200]"
              data-testid="lp-contact-success"
            >
              <div className="flex items-center gap-3 uppercase-mono text-[#CCFF00]">
                <Check size={16} strokeWidth={1.5} /> message received
              </div>
              <div className="font-serif text-2xl sm:text-3xl mt-3">
                Thanks — we&apos;ll be in touch within 48h.
              </div>
              <div className="text-[#8a8a8a] mt-3 text-sm">
                Reply lands from a real person &mdash; Watch your inbox!
              </div>
            </div>
          ) : (
            <form
              onSubmit={onContactSubmit}
              className="mt-10 border border-[#222] p-6 sm:p-8 lg:p-10 space-y-5"
              data-testid="lp-contact-form"
            >
              {/* Honeypot — hidden from humans, visible to bots */}
              <div className="hidden" aria-hidden="true">
                <label>
                  Company website (leave blank)
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={contactCompanyWebsite}
                    onChange={(e) => setContactCompanyWebsite(e.target.value)}
                    data-testid="lp-contact-hp"
                  />
                </label>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <label className="block">
                  <div className="uppercase-mono text-[#8a8a8a] mb-2">Your name</div>
                  <input
                    type="text"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    placeholder="Your Name"
                    data-testid="lp-contact-name"
                    required
                    className="w-full px-3 py-3 text-sm"
                  />
                </label>
                <label className="block">
                  <div className="uppercase-mono text-[#8a8a8a] mb-2">Email</div>
                  <input
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    placeholder="you@studio.com"
                    data-testid="lp-contact-email"
                    required
                    className="w-full px-3 py-3 text-sm font-mono"
                  />
                </label>
              </div>

              <label className="block">
                <div className="uppercase-mono text-[#8a8a8a] mb-2">Message</div>
                <textarea
                  rows={6}
                  value={contactMessage}
                  onChange={(e) => setContactMessage(e.target.value)}
                  placeholder="Tell us what you&apos;re working on, what you&apos;d like from us, or just say hi."
                  data-testid="lp-contact-message"
                  required
                  maxLength={4000}
                  className="w-full px-3 py-3 text-sm leading-relaxed resize-y"
                />
                <div className="uppercase-mono text-[#555] mt-1 text-[10px] text-right">
                  {contactMessage.length}/4000
                </div>
              </label>

              <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                <div className="uppercase-mono text-[#555] text-[11px]">
                  Delivered to two humans · no auto-responder
                </div>
                <button
                  type="submit"
                  disabled={contactSubmitting}
                  data-testid="lp-contact-submit"
                  className="btn-accent px-6 py-3 uppercase tracking-widest text-[12px] inline-flex items-center gap-2 disabled:opacity-50"
                >
                  {contactSubmitting ? (
                    <Loader2 size={14} className="animate-spin" strokeWidth={1.5} />
                  ) : (
                    <MessageSquare size={14} strokeWidth={1.5} />
                  )}
                  {contactSubmitting ? "sending\u2026" : "Send message"}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="px-4 sm:px-6 lg:px-12 py-8 sm:py-10 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="font-serif text-xl">
          Vantage<span className="text-[#CCFF00]"> </span>Hub
        </div>
        <div className="uppercase-mono text-[#555] text-center text-[10px] sm:text-[11px]">
          A tool for people who&apos;d rather be making the work.
        </div>
      </footer>
    </div>
  );
}
